import type { SourcePostDto } from '@mpr/shared';

import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';

/**
 * A source post rendered as a card rather than a table row.
 *
 * A table made every post look identical — the operator had to read ids to tell
 * them apart. Showing the image, the text and the media badges makes the post
 * recognisable at a glance, which is what matters when choosing one to
 * republish.
 */
export function PostCard({
  post,
  onSelect,
  selected,
}: {
  post: SourcePostDto;
  onSelect: (post: SourcePostDto) => void;
  selected?: boolean;
}) {
  const { t } = useI18n();
  const fmt = useFormatters();

  const photos = post.media.filter((m) => m.type === 'photo' && m.url);
  const cover = photos[0]?.url ?? null;

  // A post is only reproducible if every attachment is a photo we can
  // re-upload. Video has no downloadable source through the Graph API.
  const unsupported = post.media.filter((m) => !(m.type === 'photo' && m.downloadable));
  const reproducible = unsupported.length === 0;

  return (
    <article className={`post-card${selected ? ' selected' : ''}`}>
      {cover ? (
        <div className="post-cover">
          <img src={cover} alt="" loading="lazy" />
          {photos.length > 1 && <span className="cover-count">+{photos.length - 1}</span>}
        </div>
      ) : (
        <div className="post-cover post-cover-empty">
          <Icon.image />
        </div>
      )}

      <div className="post-body">
        <p className="post-text">
          {post.message ?? <span className="muted">{t('common.noText')}</span>}
        </p>

        <div className="post-meta">
          <span>{fmt.date(post.createdTime)}</span>
          <span className="badge accent">{post.contentType}</span>
          {post.media.map((m, i) => (
            <span key={i} className={`badge ${m.downloadable ? 'ok' : 'warn'}`}>
              {m.type}
            </span>
          ))}
        </div>

        <div className="post-actions">
          <button
            type="button"
            className="primary small"
            onClick={() => onSelect(post)}
            disabled={!reproducible}
            title={reproducible ? t('common.select') : t('source.notReproducibleHint')}
          >
            {selected ? (
              <>
                <Icon.check />
                {t('common.selected')}
              </>
            ) : (
              t('common.select')
            )}
          </button>

          {post.permalinkUrl && (
            <a className="small" href={post.permalinkUrl} target="_blank" rel="noreferrer noopener">
              {t('source.openInFacebook')} <Icon.external />
            </a>
          )}
        </div>

        <span className="mono tiny muted">{post.externalId}</span>

        {!reproducible && (
          <p className="warn tiny">
            {t('source.notReproducible', {
              types: unsupported.map((m) => m.type).join(', '),
            })}
          </p>
        )}
      </div>
    </article>
  );
}
